import { inject, Injectable } from '@angular/core';
import { Howl } from 'howler';

import { SOUNDS_LIST } from '@app/sounds-list';

import { isArray, isEmpty } from '../helpers';
import { BrowserApiService } from '../common';

import { SOUNDS_CONFIG } from './sounds.tokens';
import { SoundsState } from './sounds.state';

interface SoundCache {
  instance: Howl;
  lastPlayed: number;
}

@Injectable({
  providedIn: 'root'
})
export class SoundsService {
  private readonly config = inject(SOUNDS_CONFIG);
  private readonly soundsState = inject(SoundsState);
  private readonly isBrowser = inject(BrowserApiService).isBrowser();
  private readonly isIOS = this.isBrowser && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  // Кэш звуковых объектов для повторного использования
  private readonly soundCache: Map<string, SoundCache> = new Map();

  // Очередь для частых звуков (например, tick звуки колеса)
  private readonly soundThrottles: Map<string, number> = new Map();

  // Минимальный интервал между одинаковыми короткими звуками (в мс)
  private readonly throttleInterval: Record<string, number> = {
    'wheel-spinner-click': 90, // для колеса
    'jackpot-spinner-click': 90, // для джекпота
    default: 50 // для других звуков по умолчанию
  };

  // Максимальное количество активных звуков одновременно
  private readonly maxActiveSounds = this.isIOS ? 3 : 8;

  // Текущее количество активных звуков
  private activeSoundsCount = 0;

  constructor() {
    // Предзагрузка часто используемых звуков
    if (this.isIOS) {
      this.preloadCommonSounds();
    }

    // Очистка неиспользуемых звуков каждые 30 секунд
    if (this.isBrowser) {
      setInterval(() => this.cleanupUnusedSounds(), 30000);
    }
  }

  /**
   * Воспроизведение звука с оптимизацией для повторных вызовов
   */
  playSound(id: keyof typeof SOUNDS_LIST, volume?: number, loop?: boolean): Howl | null {
    if (!this.isBrowser || this.soundsState.isMuted()) {
      return null;
    }

    // Проверка троттлинга для часто повторяющихся звуков
    if (this.shouldThrottleSound(id)) {
      return null;
    }

    // Получаем или создаем звуковой объект
    const sound = this.getSoundInstance(id, volume, loop);

    if (!sound) {
      return null;
    }

    // Воспроизводим звук
    sound.play();

    return sound;
  }

  /**
   * Получает существующий или создает новый экземпляр звука
   */
  // eslint-disable-next-line max-statements
  private getSoundInstance(id: keyof typeof SOUNDS_LIST, volume?: number, loop?: boolean): Howl | null {
    const cacheKey = `${id}-${loop ? 'loop' : 'once'}`;
    const currentTime = Date.now();
    const currentVolume = this.soundsState.volume();

    // Настройка финального значения громкости
    const finalVolume = volume && volume > 0 && volume <= 1 ? Math.min(volume, currentVolume) : currentVolume;

    // Проверяем кэш
    if (this.soundCache.has(cacheKey)) {
      const cached = this.soundCache.get(cacheKey)!;

      cached.lastPlayed = currentTime;

      // Обновляем громкость
      cached.instance.volume(finalVolume);

      // Если звук зациклен, но сейчас остановлен
      if (loop && !cached.instance.playing()) {
        return cached.instance;
      }

      // Для незацикленных звуков сбросим позицию
      if (!loop) {
        cached.instance.stop();

        return cached.instance;
      }

      return cached.instance;
    }

    // Проверка на достижение лимита активных звуков
    if (this.activeSoundsCount >= this.maxActiveSounds) {
      this.removeOldestSound();
    }

    // Создаем новый звук
    const { soundsDir } = this.config;
    const fileNames: string[] | undefined = this.config.soundsList[id];

    if (isEmpty(fileNames) || !isArray(fileNames)) {
      return null;
    }

    const src: string[] = [];

    this.config.soundsList[id].forEach((fileName: string) => src.push(`${soundsDir}/${fileName}`));

    // Создаем звуковой объект с оптимальными настройками
    const sound = new Howl({
      src,
      volume: finalVolume,
      loop,
      html5: true,
      preload: true,
      pool: this.isIOS ? 1 : 5
    });

    // Обработчики событий
    sound.once('unlock', () => {
      if (!loop) {
        sound.stop();
      }
    });

    sound.once('end', () => {
      sound.off('unlock');

      if (!loop) {
        this.activeSoundsCount--;
      }
    });

    sound.once('play', () => {
      this.activeSoundsCount++;
    });

    // Добавляем в кэш
    this.soundCache.set(cacheKey, {
      instance: sound,
      lastPlayed: currentTime
    });

    return sound;
  }

  /**
   * Удаляет наиболее старый неиспользуемый звук для освобождения ресурсов
   */
  private removeOldestSound(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    // Находим самый старый звук
    for (const [key, cache] of this.soundCache.entries()) {
      if (!cache.instance.playing() && cache.lastPlayed < oldestTime) {
        oldestTime = cache.lastPlayed;
        oldestKey = key;
      }
    }

    // Удаляем его если нашли
    if (oldestKey) {
      const cache = this.soundCache.get(oldestKey)!;

      cache.instance.unload();
      this.soundCache.delete(oldestKey);
    } else {
      // Если все звуки активны, останавливаем самый старый
      let oldestPlayingKey: string | null = null;

      oldestTime = Infinity;

      for (const [key, cache] of this.soundCache.entries()) {
        if (!key.includes('-loop') && cache.lastPlayed < oldestTime) {
          oldestTime = cache.lastPlayed;
          oldestPlayingKey = key;
        }
      }

      if (oldestPlayingKey) {
        const cache = this.soundCache.get(oldestPlayingKey)!;

        cache.instance.stop();
        this.activeSoundsCount--;
      }
    }
  }

  /**
   * Предзагружает часто используемые звуки для iOS
   */
  private preloadCommonSounds(): void {
    // Список часто используемых звуков
    const commonSounds: Array<keyof typeof SOUNDS_LIST> = [
      'wheel-spinner-click',
      'jackpot-spinner-click',
      'success-notify'
    ];

    // Предзагружаем с нулевой громкостью
    commonSounds.forEach((id) => {
      const sound = this.getSoundInstance(id, 0, false);

      if (sound) {
        // Разблокируем звук
        document.addEventListener(
          'touchstart',
          () => {
            sound.volume(0);
            sound.play();
            sound.stop();
          },
          { once: true }
        );
      }
    });
  }

  /**
   * Проверяет, нужно ли ограничить воспроизведение звука (для часто повторяющихся)
   */
  private shouldThrottleSound(id: keyof typeof SOUNDS_LIST): boolean {
    // Троттлинг только для часто повторяющихся звуков
    const shouldThrottle = id.includes('-click') || id.includes('spinner');

    if (!shouldThrottle) {
      return false;
    }

    const now = Date.now();
    const lastPlayed = this.soundThrottles.get(String(id)) || 0;
    const interval = this.throttleInterval[String(id)] || this.throttleInterval['default'];

    if (now - lastPlayed < interval) {
      return true;
    }

    this.soundThrottles.set(String(id), now);

    return false;
  }

  /**
   * Очищает кэш от неиспользуемых звуков
   */
  private cleanupUnusedSounds(): void {
    const now = Date.now();
    const maxAge = 60000; // 60 секунд

    for (const [key, cache] of this.soundCache.entries()) {
      // Не очищаем звуки, которые воспроизводятся или использовались недавно
      if (cache.instance.playing() || now - cache.lastPlayed < maxAge) {
        continue;
      }

      cache.instance.unload();
      this.soundCache.delete(key);
    }
  }

  /**
   * Останавливает все звуки
   */
  stopAllSounds(): void {
    for (const [, cache] of this.soundCache.entries()) {
      cache.instance.stop();
    }

    this.activeSoundsCount = 0;
  }
}
